/**
 * Brand handling for the admin email template screen.
 *
 * Templates name their brand through `{{brandName}}`, `{{brandShortName}}`, `{{brandLogoUrl}}`
 * and friends, which `send-email` fills from the brand a send is made for. The stored
 * `sample_data` carries the default brand's values for them so a plain preview renders, and
 * that is what makes previewing another brand need care: sample values win over the brand
 * variables on the send path.
 */

/** Matches a `{{brand...}}` placeholder, allowing the whitespace the renderer also allows. */
const BRAND_VARIABLE = /\{\{\s*brand[A-Z]\w*\s*\}\}/;

/** Whether any part of a template names its brand, i.e. whether previewing as a brand changes it. */
export function templateUsesBrandVariables(template: {
  subject_template?: string | null;
  html_template?: string | null;
  text_template?: string | null;
}): boolean {
  return [template.subject_template, template.html_template, template.text_template].some(
    (part) => typeof part === 'string' && BRAND_VARIABLE.test(part),
  );
}

/**
 * Sample data with its `brand...` keys removed, so a test send made for a brand gets that brand's
 * values from `send-email` instead of the default brand's stored samples.
 */
export function withoutBrandVariables(sample: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(sample).filter(([key]) => !/^brand[A-Z]/.test(key)));
}

/**
 * The display name of a `From:` header -- `Kaleido` from `"Kaleido" <billing@bhcskids.com>`.
 *
 * Every brand sends from the same address and only the display name follows the brand, so the
 * name is what tells an admin which brand a logged email went out as. Falls back to the whole
 * header when it has no display name, and to null when there is no header to read.
 */
export function senderDisplayName(from: unknown): string | null {
  if (typeof from !== 'string' || from.trim() === '') return null;
  const match = from.match(/^\s*"?([^"<]*?)"?\s*<[^>]+>\s*$/);
  const name = match?.[1]?.trim();
  return name ? name : from.trim();
}
